// file: /src/Pages/NewReservation/StepOne/algorithm/getAvailableTimeblocks.js

const moment = require('moment-timezone');
const { timeblocksAvailable } = require('./processing/timeblocksAvailable');
const { daysOfWeekEnglish, getMealTypeByTime } = require('./restaurant_data/openinghours');

const TIMEZONE = 'Europe/Brussels';

/**
 * Helper: get English weekday name for a YYYY-MM-DD in Brussels time.
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {string} English weekday (e.g. "Monday")
 */
function getEnglishDayInBrussels(dateStr) {
  const m = moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE);
  return daysOfWeekEnglish[m.day()];
}

/**
 * Helper function to check if a time is compatible with a giftcard.
 * @param {Object} data - The restaurant data.
 * @param {string} dateStr - The date string in YYYY-MM-DD format.
 * @param {string} timeStr - The time string in HH:MM format.
 * @param {string} giftcard - The selected giftcard.
 * @returns {boolean} - True if the time is compatible with the giftcard, false otherwise.
 */
function timeHasGiftcard(data, dateStr, timeStr, giftcard) {
  if (!giftcard || !giftcard.trim()) return true; // No giftcard => no restriction

  const mealType = getMealTypeByTime(timeStr);
  if (!mealType) return false;

  const englishDay = getEnglishDayInBrussels(dateStr);
  const ohKey = `openinghours-${mealType}`;
  const daySettings =
    data[ohKey] &&
    data[ohKey].schemeSettings &&
    data[ohKey].schemeSettings[englishDay]
      ? data[ohKey].schemeSettings[englishDay]
      : null;

  if (!daySettings || daySettings.giftcardsEnabled !== true) return false;
  if (!Array.isArray(daySettings.giftcards) || daySettings.giftcards.length === 0) return false;

  return daySettings.giftcards.includes(giftcard);
}

/**
 * Parses a time string in "HH:MM" format into a Date object on a specific date
 * interpreted in the Brussels timezone.
 * @param {string} dateStr - The date string in YYYY-MM-DD format.
 * @param {string} timeStr - Time string in HH:MM format.
 * @returns {Date} Date object representing the time on the specified date in Brussels time.
 */
function parseDateTimeInBrussels(dateStr, timeStr) {
  return moment.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm', TIMEZONE).toDate();
}

/**
 * Gets the available time blocks or shifts for a reservation, considering
 * 'uurOpVoorhand', 'dagenInToekomst', giftcard restrictions, and now also
 * filtering by allowedTimes on the currently selected menu item—only when
 * normalOpeningTimes = false ("Nee").
 *
 * @param {Object} data - The main data object containing settings and meal information.
 * @param {string} dateStr - The date string in YYYY-MM-DD format.
 * @param {Array} reservations - An array of reservation objects.
 * @param {number} guests - The number of guests for the reservation.
 * @param {string|null} selectedGiftcard - The selected giftcard (if any).
 * @returns {Object} - Returns a pruned object of available time blocks.
 */
function getAvailableTimeblocks(
  data,
  dateStr,
  reservations,
  guests,
  selectedGiftcard = null
) {
  // 1) Determine 'uurOpVoorhand' and 'dagenInToekomst'
  let uurOpVoorhand = parseInt(data['general-settings']?.uurOpVoorhand, 10);
  if (isNaN(uurOpVoorhand) || uurOpVoorhand < 0) uurOpVoorhand = 4;

  let dagenInToekomst = parseInt(data['general-settings']?.dagenInToekomst, 10);
  if (isNaN(dagenInToekomst) || dagenInToekomst <= 0) dagenInToekomst = 90;

  // "Now" anchored to Brussels time
  const nowBrussels = moment.tz(TIMEZONE);

  const maxAllowedDateEndOfDay = nowBrussels.clone().add(dagenInToekomst, 'days').endOf('day');

  const targetStartOfDay = moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE).startOf('day');

  if (targetStartOfDay.isAfter(maxAllowedDateEndOfDay)) {
    return {};
  }

  const isToday = targetStartOfDay.isSame(nowBrussels, 'day');

  // 2) Get base timeblocks
  const availableTimeblocks = timeblocksAvailable(
    data,
    dateStr,
    reservations,
    guests
  );

  // 3) Filter by giftcard compatibility
  if (selectedGiftcard) {
    for (const time of Object.keys(availableTimeblocks)) {
      if (!timeHasGiftcard(data, dateStr, time, selectedGiftcard)) {
        delete availableTimeblocks[time];
      }
    }
  }

  // 4) Prune past times if 'uurOpVoorhand' applies to today
  if (isToday && uurOpVoorhand >= 0) {
    const cutoff = nowBrussels.clone().add(uurOpVoorhand, 'hours'); // Brussels cutoff

    for (const time of Object.keys(availableTimeblocks)) {
      const slotMoment = moment.tz(`${dateStr} ${time}`, 'YYYY-MM-DD HH:mm', TIMEZONE);
      if (slotMoment.isBefore(cutoff)) {
        delete availableTimeblocks[time];
      }
    }
  }

  // 5) **NEW** Only filter by allowedTimes when normalOpeningTimes is false ("Nee")
  const currentItem = typeof window !== 'undefined' ? window.currentReservationTicketItem : null;
  if (
    currentItem &&
    !currentItem.normalOpeningTimes &&
    Array.isArray(currentItem.allowedTimes) &&
    currentItem.allowedTimes.length > 0
  ) {
    const allowedList = currentItem.allowedTimes
      .map((t) => t.time)
      .filter(Boolean);
    for (const time of Object.keys(availableTimeblocks)) {
      if (!allowedList.includes(time)) {
        delete availableTimeblocks[time];
      }
    }
  }

  return availableTimeblocks;
}

module.exports = {
  getAvailableTimeblocks,
  timeHasGiftcard
};
